import { DOCUMENT } from '@angular/common';
import { ProductCard } from '../../../shared/product-card/product-card';
import {
  Component,
  DestroyRef,
  ElementRef,
  ViewChild,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SAVING_ARENA_API_CONFIG } from '../../../core/api/api.config';
import {
  FrontendBlog,
  FrontendCoupon,
  FrontendCouponCategory,
  FrontendEvent,
  FrontendStore,
} from '../../../core/api/api.models';
import { blogReadMinutes } from '../../../core/api/blog-text';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { TrustedCmsHtmlPipe } from '../../../core/api/trusted-cms-html.pipe';
import { BigSaleBanner } from '../../../shared/big-sale-banner/big-sale-banner';
import { SeoService } from '../../../core/seo/seo.service';
import { SsrResponseService } from '../../../core/seo/ssr-response.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import {
  faqPageSchema,
  itemListSchema,
  offerSchema,
  webPageSchema,
} from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { StoreLogo } from '../../../shared/store-logo/store-logo';
import { AutoScrollRow } from '../../../shared/auto-scroll-row/auto-scroll-row';
import { AffiliateLinkService } from '../../../core/affiliate/affiliate-link.service';
import { formatOfferCount } from '../../../core/format/offer-count';

// A store assigned to the event (admin "Event Top Stores"), shaped for the top-stores cards/logos.
interface EventTopStore {
  name: string;
  logoUrl: string;
  path: string;
  couponsAndDealsCount: string;
}

// Category-wise event coupons: main category → sub-category tabs → coupon cards. All built
// dynamically from the coupons assigned to the event (grouped by their coupon category).
/**
 * One coupon card in an event section.
 *
 * `id` and `storeSlug` exist for the CTA: every coupon here belongs to a different merchant, so both
 * destinations (the offer popup and the redirect) are built from the coupon's own store, not
 * from the event.
 */
interface EventCouponCard {
  id: string;
  storeSlug: string;
  store: string;
  logoUrl: string;
  storePath: string;
  title: string;
  expires: string;
  isDeal: boolean;
  actionLabel: string;
}

interface EventCouponSubTab {
  id: string;
  label: string;
  coupons: EventCouponCard[];
}

interface EventCouponSection {
  id: string;
  name: string;
  heading: string;
  viewMorePath: string;
  viewMoreLabel: string;
  tabs: EventCouponSubTab[];
}

// An entry in the sticky category menu band. Built from the coupon sections present on the page;
// clicking one scrolls to that section.
interface EventMenuCategory {
  id: string;
  name: string;
}

// A chip in the "Seasonal Shopping Events" section — every published event, linking to its page.
interface SeasonalEvent {
  name: string;
  path: string;
}

// A product assigned to the event (admin Product → Select Event) → the Essentials cards.
interface EventProductCard {
  name: string;
  description: string;
  newPrice: string;
  oldPrice: string;
  imageUrl: string;
  hoverImageUrl: string;
  /** Whether the product goes off-site, so the card builds its own /go/p/ redirect. */
  isExternal: boolean;
  /** Needed for the card's /go/p/:id redirect. Without it these clicks bypassed it entirely. */
  id: string;
  storeName: string;
  storeLogoUrl: string;
  storePath: string;
}

// A blog assigned to the event (admin Blog → Select Event) → the Insights/Journal cards.
interface EventBlogCard {
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  imageUrl: string;
  author: string;
  path: string;
}

type PopularCategoryThumb = 'men' | 'women' | 'accessories' | 'outerwear' | 'shoes';

// Shown while the event loads or if it can't be found, so the hero never renders empty.
/**
 * The name to show before the event has loaded, derived from the slug in the URL.
 *
 * This used to be the constant 'Black Friday Sales'. That meant every event page, on every
 * hydration, briefly announced itself as Black Friday: the Autumn Sale page said "Black Friday
 * Sales", its section headings said "Black Friday Top Stores", and only once the API answered did
 * the real name appear. The slug is already in the URL and is already the event's own name, so
 * there is nothing to guess at.
 */
function titleFromSlug(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
// Character budget before the long description collapses behind a "Read More" toggle.
const LONGFORM_PREVIEW_LIMIT = 500;

interface PopularCategoryItem {
  label: string;
  /** Legacy stock thumbnail keyed by name; only used when the category has no real image. */
  thumb?: PopularCategoryThumb;
  slug?: string;
  /** Category page this tile links to. */
  path?: string;
  /** The admin's category icon, or the category's newest product image. */
  imageUrl?: string;
  /** Inline SVG the admin stored instead of an image. */
  iconHtml?: SafeHtml | null;
}

interface PopularCategoryTab {
  id: string;
  label: string;
  categories: PopularCategoryItem[];
}

@Component({
  selector: 'app-event-detail-page',
  imports: [
    Breadcrumb,
    RouterLink,
    TrustedCmsHtmlPipe,
    ProductCard,
    BigSaleBanner,
    StoreLogo,
    AutoScrollRow,
  ],
  templateUrl: './event-detail-page.html',
  styleUrl: './event-detail-page.scss',
})
export class EventDetailPage {
  @ViewChild('eventMenuViewport') private readonly eventMenuViewport?: ElementRef<HTMLDivElement>;

  private readonly route = inject(ActivatedRoute);
  private readonly affiliate = inject(AffiliateLinkService);
  private readonly api = inject(SavingArenaApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);
  private readonly ssrResponse = inject(SsrResponseService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly document = inject(DOCUMENT);
  private readonly sanitizer = inject(DomSanitizer);

  // Live event loaded from the admin panel by its slug (:eventslug). The hero H1, short/long
  // description, and page meta tags are driven from here; the rest of the page is still static.
  protected readonly event = signal<FrontendEvent | null>(null);

  /** The last crumb is the event's name, which is only known once the event has loaded. */
  protected readonly crumbs = computed(() => {
    const name = this.event()?.name?.trim() || '';
    return breadcrumbTrail({ label: 'Events', path: '/events' }, ...(name ? [{ label: name }] : []));
  });
  /** The :eventslug currently being viewed, available before the API has answered. */
  protected readonly eventSlug = signal('');

  /**
   * The event's name: the loaded one when it is there, otherwise the slug turned back into words.
   * Every heading on this page runs through it, so none of them can name the wrong event.
   */
  protected readonly eventName = computed(
    () => this.event()?.name?.trim() || titleFromSlug(this.eventSlug()),
  );

  /**
   * The event's name with its year, for section headings only.
   *
   * The stored names carry no year on purpose, so one event survives from one year to the next and
   * keeps its URL and whatever ranking it has earned. Headings are the opposite case: "Labour Day
   * Sale 2026 Top Stores" is what people actually search for, and a heading with no year reads as
   * though it might be last year's page.
   *
   * The year comes from the event's own start date. Without one, the name is used unchanged rather
   * than guessing at the current year, which would be wrong for an event that has not started.
   */
  protected readonly eventNameWithYear = computed(() => {
    const name = this.eventName();
    const start = this.event()?.startDate;
    const year = start ? new Date(start).getUTCFullYear() : NaN;

    return Number.isFinite(year) && !name.includes(String(year)) ? `${name} ${year}` : name;
  });

  private readonly eventContent = computed(
    () => (this.event()?.content ?? undefined) as Record<string, unknown> | undefined,
  );

  // H1 → admin content heading, falling back to the event name.
  protected readonly eventTitle = computed(
    () =>
      this.getContentString(this.eventContent(), 'headingH1') ||
      this.event()?.name?.trim() ||
      this.eventName(),
  );

  // Short description → the concise summary shown under the H1.
  protected readonly eventShortDescription = computed(
    () =>
      this.getContentString(this.eventContent(), 'shortContent') ||
      this.getContentString(this.eventContent(), 'shortDescription'),
  );

  // Long description → admin CMS HTML rendered in the guide section.
  protected readonly eventLongHtml = computed(
    () =>
      this.getContentHtml(this.eventContent(), 'longContent') ||
      this.getContentHtml(this.eventContent(), 'longDescription') ||
      this.getContentHtml(this.eventContent(), 'description'),
  );
  protected readonly hasEventLongContent = computed(() => this.eventLongHtml().length > 0);
  private readonly eventLongPlainText = computed(() => this.cleanText(this.eventLongHtml()));

  // Top stores assigned to this event in the admin panel (EventData.topStoresFeatured / topStores).
  private readonly eventData = computed(() => {
    const data = this.event()?.data;
    return data && typeof data === 'object' && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : null;
  });

  protected readonly topStoresHeading = computed(
    () => `${this.eventNameWithYear()} Top Stores`,
  );
  // Featured stores → the circular logo row under the "{Event} Top Stores" heading.
  protected readonly topStoresFeatured = computed(() =>
    this.mapEventStores(this.eventData()?.['topStoresFeatured']),
  );
  // Regular top stores → the store cards grid.
  protected readonly topStores = computed(() =>
    this.mapEventStores(this.eventData()?.['topStores']),
  );
  protected readonly hasTopStoresFeatured = computed(() => this.topStoresFeatured().length > 0);
  protected readonly hasTopStores = computed(() => this.topStores().length > 0);

  // Top-pick coupons assigned to this event → the "Member Perks" partner-coupons cards.
  protected readonly topPickCoupons = computed(() =>
    this.mapEventCoupons(this.eventData()?.['topPickCoupons']),
  );
  protected readonly hasTopPickCoupons = computed(() => this.topPickCoupons().length > 0);

  // Stores that have this event assigned on their own record (admin Add/Edit Store → Select Event)
  // → the "{Event} Stores" grid.
  protected readonly eventStoresHeading = computed(
    () => `${this.eventNameWithYear()} Stores`,
  );
  protected readonly eventStores = computed<EventTopStore[]>(() =>
    (this.event()?.stores ?? []).map((store) => this.toEventAssignedStore(store)),
  );
  protected readonly hasEventStores = computed(() => this.eventStores().length > 0);

  // Blogs assigned to this event (admin Blog → Select Event) → the Insights/Journal cards.
  protected readonly eventBlogsHeading = computed(
    () => `${this.eventNameWithYear()} Insights & Guides`,
  );
  protected readonly eventBlogs = computed<EventBlogCard[]>(() =>
    (this.event()?.blogs ?? []).map((blog) => this.toEventBlogCard(blog)),
  );
  protected readonly hasEventBlogs = computed(() => this.eventBlogs().length > 0);

  // Products assigned to this event (admin Product → Select Event) → the "Essentials" cards.
  protected readonly eventProductsHeading = computed(
    () => `${this.eventNameWithYear()} Essentials`,
  );
  protected readonly eventProducts = computed<EventProductCard[]>(() => {
    const products = this.event()?.['products'];
    if (!Array.isArray(products)) {
      return [];
    }
    return products
      .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
      .map((product) => this.toEventProductCard(product));
  });
  protected readonly hasEventProducts = computed(() => this.eventProducts().length > 0);

  // FAQs assigned to this event in the admin panel (Events → Event Content → FAQ).
  protected readonly eventFaqsHeading = computed(
    () => `${this.eventNameWithYear()} FAQs`,
  );
  protected readonly eventFaqs = computed(() =>
    (this.event()?.faqs ?? []).filter((faq) => (faq.question ?? '').trim().length > 0),
  );
  protected readonly hasEventFaqs = computed(() => this.eventFaqs().length > 0);

  // Category-wise event coupons: one section per main category, with sub-category tabs inside.
  protected readonly eventCouponSections = computed<EventCouponSection[]>(() =>
    this.buildEventCouponSections(this.event()?.coupons),
  );
  // Selected sub-tab per section (keyed by section id); defaults to each section's first tab.
  private readonly selectedCouponTabBySection = signal<Record<string, string>>({});

  protected selectedCouponTab(section: EventCouponSection): string {
    return this.selectedCouponTabBySection()[section.id] ?? section.tabs[0]?.id ?? '';
  }

  protected selectCouponTab(sectionId: string, tabId: string): void {
    this.selectedCouponTabBySection.update((current) => ({ ...current, [sectionId]: tabId }));
  }

  protected visibleCoupons(section: EventCouponSection): EventCouponCard[] {
    const tabId = this.selectedCouponTab(section);
    return section.tabs.find((tab) => tab.id === tabId)?.coupons ?? section.tabs[0]?.coupons ?? [];
  }

  // The sticky menu band lists the coupon categories present on the page ("Top Picks" first when
  // there are featured coupons). Each entry anchors to its section id (see the `event-cat-*` ids).
  protected readonly menuCategories = computed<EventMenuCategory[]>(() => {
    const items: EventMenuCategory[] = [];

    if (this.hasTopPickCoupons()) {
      items.push({ id: 'top-picks', name: 'Top Picks' });
    }

    for (const section of this.eventCouponSections()) {
      items.push({ id: section.id, name: section.name });
    }

    return items;
  });

  private readonly activeMenuCategoryId = signal<string>('');

  protected isMenuCategoryActive(category: EventMenuCategory): boolean {
    const active = this.activeMenuCategoryId() || this.menuCategories()[0]?.id;
    return active === category.id;
  }

  protected selectMenuCategory(category: EventMenuCategory): void {
    this.activeMenuCategoryId.set(category.id);
    this.scrollActiveMenuIntoView(category.id);
    this.document
      .getElementById(`event-cat-${category.id}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Scroll-spy: highlight the menu item whose category section is currently sitting just below
   * the sticky menu band. Iterates the menu categories in DOM order and keeps the last one whose
   * top has scrolled above the band's bottom edge — that's the section the reader is looking at.
   */
  private syncActiveMenuOnScroll(): void {
    const categories = this.menuCategories();
    if (categories.length === 0) {
      return;
    }

    const band = this.document.querySelector('.sav-event-detail-menu-band') as HTMLElement | null;
    // Detection line: a little below the sticky band so a section counts as "active" once its
    // heading clears the band. Falls back to a sane offset if the band isn't found.
    const line = (band?.getBoundingClientRect().bottom ?? 160) + 24;

    let activeId = categories[0].id;
    for (const category of categories) {
      const section = this.document.getElementById(`event-cat-${category.id}`);
      if (section && section.getBoundingClientRect().top <= line) {
        activeId = category.id;
      }
    }

    if (activeId !== (this.activeMenuCategoryId() || categories[0].id)) {
      this.activeMenuCategoryId.set(activeId);
      this.scrollActiveMenuIntoView(activeId);
    }
  }

  /**
   * Put each coupon's store and category objects back.
   *
   * The event endpoint sends every store and category once (`couponStores`, `couponCategories`) and
   * has the coupons point at them by `storeKey` / `categoryKey`: repeated on 500 coupons they were
   * ~120KB of the hydration state embedded in the page. Coupons that still carry the objects inline
   * pass through untouched, so this reads either shape.
   */
  private expandEventCoupons(event: FrontendEvent): FrontendEvent {
    const stores = this.toRecord(event['couponStores']) ?? {};
    const categories = this.toRecord(event['couponCategories']) ?? {};
    if (!event.coupons?.length || (!Object.keys(stores).length && !Object.keys(categories).length)) {
      return event;
    }

    const coupons = event.coupons.map((coupon) => {
      const storeKey = typeof coupon['storeKey'] === 'string' ? coupon['storeKey'] : '';
      const categoryKey = typeof coupon['categoryKey'] === 'string' ? coupon['categoryKey'] : '';
      return {
        ...coupon,
        store: coupon.store ?? (storeKey ? (stores[storeKey] as FrontendStore | undefined) : undefined),
        category:
          coupon.category ??
          (categoryKey ? (categories[categoryKey] as FrontendCouponCategory | undefined) : undefined),
      };
    });

    return { ...event, coupons };
  }

  /** Keep the active menu button visible inside the horizontal menu carousel. */
  private scrollActiveMenuIntoView(categoryId: string): void {
    const button = this.document.getElementById(`event-menu-${categoryId}`);
    button?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  constructor() {
    this.route.paramMap
      .pipe(
        map((params) => params.get('eventslug')?.trim() ?? ''),
        tap((slug) => this.eventSlug.set(slug)),
        switchMap((slug) =>
          slug
            ? this.api.getEvent(slug).pipe(
                map((response) => (response.data ? this.expandEventCoupons(response.data) : response.data)),
                catchError((error: unknown) => {
                  this.ssrResponse.noteFetchFailure(error);
                  return of(null);
                }),
              )
            : of(null),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.event.set(event);
        this.applyEventSeo(event);
      });

    this.loadSeasonalEvents();

    // The category menu band sticks below the site header. Measure the header's real height
    // (varies by breakpoint/content) and expose it as a CSS variable so the band never overlaps
    // or leaves a gap under the header — instead of relying on a fragile fixed pixel offset.
    afterNextRender(() => {
      this.syncHeaderOffset();
      this.syncActiveMenuOnScroll();

      const onResize = () => this.syncHeaderOffset();
      window.addEventListener('resize', onResize);

      // Scroll-spy for the category menu band, throttled to one update per animation frame.
      // The header offset is refreshed on the same tick: measuring it once on render left a strip
      // of page content showing between the header and this band, because the header's height is
      // not final at that point — web fonts have not swapped in and the search row has not settled.
      let scrollTick = false;
      const onScroll = () => {
        if (scrollTick) {
          return;
        }
        scrollTick = true;
        requestAnimationFrame(() => {
          scrollTick = false;
          this.syncHeaderOffset();
          this.syncActiveMenuOnScroll();
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });

      // Fonts change the header's height when they swap in, and that fires neither resize nor
      // scroll — so without this the gap simply stayed until the visitor happened to scroll.
      this.document.fonts?.ready.then(() => this.syncHeaderOffset());

      this.destroyRef.onDestroy(() => {
        window.removeEventListener('resize', onResize);
        window.removeEventListener('scroll', onScroll);
      });
    });
  }

  private loadSeasonalEvents(): void {
    this.api
      .getEvents({ page: 1, limit: 100 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.seasonalEvents.set(
            response.data
              .filter((event) => (event.name ?? '').trim().length > 0)
              .map((event) => ({
                name: event.name,
                path: event.slug ? `/event/${event.slug}` : '#',
              })),
          );
        },
        error: () => undefined,
      });
  }

  /**
   * Pin the category menu band to the header's actual bottom edge.
   *
   * Measures `getBoundingClientRect().bottom`, not `offsetHeight`. They agree while the header is
   * parked at the top of the viewport, but the rect is what the band actually has to meet — and it
   * stays correct if the header ever scrolls partway out, where a height would leave the band
   * floating with a strip of page content visible above it.
   *
   * Rounded down, because a fractional offset renders as a hairline of background between the two.
   */
  private syncHeaderOffset(): void {
    // `[data-site-header]`, not `.sav-site-header`: that class is styled in main-layout.scss but no
    // template has ever carried it, so this lookup always returned null and the variable was never
    // written. The band fell back to the hardcoded 156px in the stylesheet — close enough to look
    // right at the top of the page, and about 27px short once the promo bar scrolled away, which is
    // where the gap came from.
    const header = this.document.querySelector('[data-site-header]') as HTMLElement | null;
    if (!header) return;

    const bottom = Math.max(0, Math.floor(header.getBoundingClientRect().bottom));
    if (bottom <= 0) return;

    const current = this.document.documentElement.style.getPropertyValue('--event-menu-top');
    const next = `${bottom}px`;
    // Only write when it actually moved: this runs on every scroll frame, and setting a custom
    // property on <html> invalidates style for the whole document.
    if (current !== next) {
      this.document.documentElement.style.setProperty('--event-menu-top', next);
    }
  }

  /**
   * SEO for an event page. The admin's Meta Title / Meta Description stay the source of truth (as
   * before); SeoService adds the canonical, OG/Twitter tags and structured data that the previous
   * manual Title/Meta calls never emitted.
   *
   * Runs inside the subscribe that resolves the event, so the tags are written before SSR
   * serialises the HTML.
   *
   * Event schema (`startDate`/`endDate`) is deliberately not emitted: schema.org `Event` describes a
   * real-world happening with a location, not a retail sale window.
   */
  private applyEventSeo(event: FrontendEvent | null): void {
    if (!event) {
      // An unknown :eventslug is a real 404, not a 200 rendering the fallback hero.
      this.ssrResponse.setNotFound();
      this.seo.apply({
        title: 'Event not found | SavingArena',
        description: 'The sales event you are looking for is not available on SavingArena.',
        noindex: true,
      });
      this.seo.setJsonLd([]);
      return;
    }

    const content = this.eventContent();
    const name = event.name?.trim() || this.eventTitle();
    const slug = event.slug?.trim() || event.id;
    const path = `/event/${slug}`;
    const url = this.seo.absolute(path);

    const title = this.getContentString(content, 'metaTitle') || this.eventTitle();
    const description =
      this.getContentString(content, 'metaDescription') ||
      this.eventShortDescription() ||
      `${name} coupons, promo codes and deals, verified by SavingArena.`;

    this.seo.apply({
      title,
      description,
      canonicalPath: path,
      image: event.thumbnailImagePath ? this.assetUrl(event.thumbnailImagePath) : undefined,
    });

    // FAQPage only when the event's FAQs are actually rendered — the template gates its FAQ section
    // on `hasEventFaqs()`, i.e. the same list.
    const faqs = this.eventFaqs()
      .map((faq) => ({
        question: this.cleanText(faq.question ?? ''),
        answer: this.cleanText(faq.answer ?? ''),
      }))
      .filter((faq) => faq.question && faq.answer);

    const offers = this.renderedOfferCards()
      .slice(0, 20)
      .map((item) =>
        offerSchema({ name: item.title, url: this.seo.absolute(item.storePath), storeName: item.store }),
      );

    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url,
        name: title,
        description,
        dateModified: typeof event.updatedAt === 'string' ? event.updatedAt : undefined,
      }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
      offers.length ? itemListSchema(offers, `${this.eventNameWithYear()} coupons and deals`) : null,
      faqs.length ? faqPageSchema(faqs) : null,
    ]);
  }

  /**
   * The coupon cards in the server-rendered HTML, in page order: the top picks, then each category
   * section's default tab. Other tabs are not in the DOM until clicked, and Offer schema must only
   * describe offers a reader can see.
   */
  private renderedOfferCards(): EventCouponCard[] {
    const seen = new Set<string>();
    const cards = [
      ...this.topPickCoupons(),
      ...this.eventCouponSections().flatMap((section) => section.tabs[0]?.coupons ?? []),
    ];
    return cards.filter((card) => {
      if (!card.title || !card.store || card.storePath === '#' || seen.has(card.id)) return false;
      seen.add(card.id);
      return true;
    });
  }

  protected readonly eventMenuItems: string[] = [
    'Top Picks',
    'Clothing, Shoes & Accessories',
    'Beauty & Health',
    'Home & Garden',
    'Food & Gifts',
    'Toys, Tech & Electronics',
    'Flights, Hotels & Travel',
    'Luxury & Designer',
    'Sports & Outdoor',
    'Baby & Kids',
    'Office & Business',
  ];

  protected readonly selectedEventMenuItem = signal(this.eventMenuItems[0]);
  protected readonly currentCategoryCouponsHeading = computed(() => {
    const eventName = this.eventNameWithYear();
    const category = this.selectedEventMenuItem();
    return category === 'Top Picks'
      ? `Best ${eventName} Top Picks Coupons & Discounts`
      : `Best ${eventName} ${category} Coupons & Discounts`;
  });

  protected readonly currentCategoryPopularHeading = computed(() => {
    const category = this.selectedEventMenuItem();
    const name = this.eventNameWithYear();
    return category === 'Top Picks'
      ? `${name} Top Picks Categories`
      : `${name} ${category} Categories`;
  });

  // All published events → the "Seasonal Shopping Events" chips (each links to its event page).
  protected readonly seasonalEvents = signal<SeasonalEvent[]>([]);

  /**
   * Tab shells only — the categories inside are live, exactly as on the home page and /collection.
   *
   * These two tabs used to hold sixteen hardcoded labels ("Outerwear", "Streetwear", "Bags"…) with
   * stock thumbnails and no links, none of which matched the categories the site actually runs.
   */
  private readonly defaultPopularCategoryTabs: PopularCategoryTab[] = [
    { id: 'coupons-offers', label: 'Coupons & Offers', categories: [] },
    { id: 'products-deals', label: 'Products & Deals', categories: [] },
  ];

  /**
   * The categories this event's own coupons belong to.
   *
   * Both sources count: the coupons assigned to the event directly, and the admin's top picks. This
   * used to be the site-wide coupon-category feed, so an event's tiles listed categories that had
   * nothing to do with it.
   */
  private readonly popularCouponCategories = computed<PopularCategoryItem[]>(() =>
    this.toEventCategoryTiles(
      [
        ...(Array.isArray(this.event()?.coupons) ? (this.event()!.coupons as unknown[]) : []),
        ...(Array.isArray(this.eventData()?.['topPickCoupons'])
          ? (this.eventData()!['topPickCoupons'] as unknown[])
          : []),
      ],
      'categories',
    ),
  );

  /** Same idea for the second tab: the categories this event's own products belong to. */
  private readonly popularDealCategories = computed<PopularCategoryItem[]>(() =>
    this.toEventCategoryTiles(
      Array.isArray(this.event()?.['products']) ? (this.event()!['products'] as unknown[]) : [],
      'collection',
    ),
  );

  protected readonly popularCategoryTabs = computed<PopularCategoryTab[]>(() => {
    const coupons = this.popularCouponCategories();
    const deals = this.popularDealCategories();

    return (
      this.defaultPopularCategoryTabs
        .map((tab) => ({
          ...tab,
          categories: tab.id === 'coupons-offers' ? coupons : deals,
        }))
        // A tab with nothing behind it is worse than no tab.
        .filter((tab) => tab.categories.length > 0)
    );
  });

  protected readonly popularCardsPerSlide = 5;
  protected readonly selectedPopularCategoryTabId = signal(this.defaultPopularCategoryTabs[0].id);
  protected readonly currentPopularCategorySlide = signal(0);

  protected readonly selectedPopularCategoryTab = computed<PopularCategoryTab>(() => {
    const tabs = this.popularCategoryTabs();
    // Tabs drop out when their feed comes back empty, so the selected id can point at one that is
    // no longer there — fall back to the first that survived.
    return (
      tabs.find((tab) => tab.id === this.selectedPopularCategoryTabId()) ??
      tabs[0] ??
      this.defaultPopularCategoryTabs[0]
    );
  });

  protected readonly popularCategoryTotalSlides = computed(() =>
    Math.max(
      1,
      this.selectedPopularCategoryTab().categories.length - this.popularCardsPerSlide + 1,
    ),
  );

  protected readonly popularCategorySlideIndexes = computed(() =>
    Array.from({ length: this.popularCategoryTotalSlides() }, (_, index) => index),
  );

  protected readonly visiblePopularCategories = computed(() => {
    const start = this.currentPopularCategorySlide();
    return this.selectedPopularCategoryTab().categories.slice(
      start,
      start + this.popularCardsPerSlide,
    );
  });

  protected readonly expandedFaqIndex = signal<number | null>(null);

  get shouldShowLongformToggle(): boolean {
    return this.eventLongPlainText().length > LONGFORM_PREVIEW_LIMIT;
  }

  // The truncating preview is gone: the template prints the guide in full and CSS clamps it, so
  // the whole of it is in the server-rendered HTML. Only the toggle decision is made here.

  protected selectPopularCategoryTab(tabId: string): void {
    this.selectedPopularCategoryTabId.set(tabId);
    this.currentPopularCategorySlide.set(0);
  }

  protected selectPopularCategorySlide(index: number): void {
    this.currentPopularCategorySlide.set(index);
  }

  protected toggleFaq(index: number): void {
    this.expandedFaqIndex.update((current) => (current === index ? null : index));
  }

  protected scrollEventMenu(direction: number): void {
    const viewport = this.eventMenuViewport?.nativeElement;

    if (!viewport) {
      return;
    }

    const scrollDistance = Math.max(260, viewport.clientWidth * 0.7);
    viewport.scrollBy({
      left: direction * scrollDistance,
      behavior: 'smooth',
    });
  }

  private mapEventStores(value: unknown): EventTopStore[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
      .map((store) => this.toEventTopStore(store));
  }

  private toEventTopStore(store: Record<string, unknown>): EventTopStore {
    const name = typeof store['name'] === 'string' ? store['name'] : '';
    const slug = typeof store['slug'] === 'string' ? store['slug'].trim() : '';
    const logoPath =
      typeof store['logoImagePath'] === 'string' ? store['logoImagePath'].trim() : '';
    const coupons = typeof store['couponsCount'] === 'number' ? store['couponsCount'] : 0;
    const deals = typeof store['dealsCount'] === 'number' ? store['dealsCount'] : 0;

    return {
      name,
      logoUrl: logoPath ? this.assetUrl(logoPath) : '',
      path: slug ? `/${slug}` : '#',
      couponsAndDealsCount: formatOfferCount(coupons, deals),
    };
  }

  private toEventProductCard(product: Record<string, unknown>): EventProductCard {
    const logoPath =
      typeof product['thumbnailImagePath'] === 'string' ? product['thumbnailImagePath'].trim() : '';
    const isExternal = product['isExternal'] === true;
    const description = typeof product['description'] === 'string' ? product['description'] : '';
    const store =
      product['store'] && typeof product['store'] === 'object'
        ? (product['store'] as Record<string, unknown>)
        : {};
    const storeSlug = typeof store['slug'] === 'string' ? store['slug'].trim() : '';
    const storeLogo =
      typeof store['logoImagePath'] === 'string' ? store['logoImagePath'].trim() : '';

    return {
      name: typeof product['name'] === 'string' ? product['name'] : '',
      // Descriptions may contain CMS HTML — strip it to plain text for the card.
      description: this.cleanText(description),
      newPrice: typeof product['newPrice'] === 'string' ? product['newPrice'] : '',
      oldPrice: typeof product['oldPrice'] === 'string' ? product['oldPrice'] : '',
      imageUrl: logoPath ? this.assetUrl(logoPath) : '',
      hoverImageUrl: this.toHoverImageUrl(product['galleryImagePaths'], logoPath),
      isExternal,
      id: typeof product['id'] === 'string' ? product['id'] : String(product['id'] ?? ''),
      storeName: typeof store['name'] === 'string' ? store['name'] : '',
      storeLogoUrl: storeLogo ? this.assetUrl(storeLogo) : '',
      storePath: storeSlug ? `/${storeSlug}` : '#',
    };
  }

  // First gallery angle that genuinely differs from the thumbnail; '' when the feed gave only one.
  private toHoverImageUrl(value: unknown, mainImagePath: string): string {
    if (!Array.isArray(value)) {
      return '';
    }

    const alternate = value
      .map((path) => (typeof path === 'string' ? path.trim() : ''))
      .find((path) => path && path !== mainImagePath);

    return alternate ? this.assetUrl(alternate) : '';
  }

  private toEventBlogCard(blog: FrontendBlog): EventBlogCard {
    const slug = blog.slug?.trim() ?? '';
    const logoPath = blog.thumbnailImagePath?.trim() ?? '';
    const author = typeof blog['author'] === 'string' ? blog['author'] : '';

    return {
      title: blog.name ?? '',
      excerpt: blog.shortDescription?.trim() ?? '',
      category: blog.category?.name?.trim() ?? '',
      date: this.formatBlogDate(blog.publishAt ?? blog.updatedAt ?? ''),
      readTime: `${blogReadMinutes(blog)} min read`,
      imageUrl: logoPath ? this.assetUrl(logoPath) : '',
      author: author || 'SavingArena',
      path: slug ? `/blogs/${slug}` : '#',
    };
  }

  private formatBlogDate(iso: string): string {
    if (!iso) {
      return '';
    }

    const date = new Date(iso);
    return Number.isNaN(date.valueOf())
      ? ''
      : date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }

  private toEventAssignedStore(store: FrontendStore): EventTopStore {
    const slug = store.slug?.trim() ?? '';
    const logoPath = store.logoImagePath?.trim() ?? '';
    const coupons = typeof store.couponsCount === 'number' ? store.couponsCount : 0;
    const deals = typeof store.dealsCount === 'number' ? store.dealsCount : 0;

    return {
      name: store.name,
      logoUrl: logoPath ? this.assetUrl(logoPath) : '',
      path: slug ? `/${slug}` : '#',
      couponsAndDealsCount: `${coupons + deals}+ Coupons & Deals`,
    };
  }

  // Groups the event's coupons by main category → sub-category → cards. Main category is the
  // coupon category's parent when it is a child; otherwise the category itself. Empty mains/subs
  // never appear because sections/tabs are built only from coupons that exist.
  private buildEventCouponSections(coupons: FrontendCoupon[] | undefined): EventCouponSection[] {
    if (!Array.isArray(coupons) || coupons.length === 0) {
      return [];
    }

    interface SubAccumulator {
      label: string;
      order: number;
      coupons: EventCouponCard[];
    }
    interface MainAccumulator {
      name: string;
      slug: string;
      order: number;
      subs: Map<string, SubAccumulator>;
    }

    const mains = new Map<string, MainAccumulator>();
    let mainOrder = 0;
    let subOrder = 0;

    for (const coupon of coupons) {
      const category = coupon.category;
      // parentCategory arrives via the category's index signature (unknown); coerce it. The main
      // category is the parent when the coupon's category is a child, otherwise the category itself.
      const rawParent = category?.['parentCategory'];
      const parent =
        category?.isChild && rawParent && typeof rawParent === 'object'
          ? (rawParent as { id?: string; name?: string; slug?: string })
          : null;
      const main = parent ?? category ?? null;

      const mainId = main?.id || main?.name || '__uncategorized';
      const mainName = main?.name?.trim() || 'Deals';
      const mainSlug = main?.slug?.trim() || '';
      const subId = category?.id || category?.name || '__all';
      const subLabel = category?.name?.trim() || mainName;

      if (!mains.has(mainId)) {
        mains.set(mainId, { name: mainName, slug: mainSlug, order: mainOrder++, subs: new Map() });
      }
      const mainEntry = mains.get(mainId)!;

      if (!mainEntry.subs.has(subId)) {
        mainEntry.subs.set(subId, { label: subLabel, order: subOrder++, coupons: [] });
      }
      const bucket = mainEntry.subs.get(subId)!;
      // The index is per sub-tab, so only the first card in each tab takes the "Top Pick" treatment.
      bucket.coupons.push(this.toEventCouponCard(coupon));
    }

    return Array.from(mains.entries())
      .sort((first, second) => first[1].order - second[1].order)
      .map(([id, main]) => ({
        id,
        name: main.name,
        // Just "<Category> Deals". These headings used to repeat the event name ("Halloween Sale 2026
        // Clothing & Fashion Deals" and so on, 20+ times per page), which reads as keyword stuffing;
        // the event name already sits in the title, H1 and the top-stores and top-picks headings.
        // A category already named "... Deals" (Daily Deals) must not become "Daily Deals Deals".
        heading: /\bdeals$/i.test(main.name) ? main.name : `${main.name} Deals`,
        viewMorePath: main.slug ? `/categories/${main.slug}` : '#',
        viewMoreLabel: `View More ${main.name} Coupons`,
        // Largest tab first. Only the default (first) tab is in the rendered HTML, so ordering by
        // first appearance let the API's coupon order decide it: a 1-coupon tab once hid 224 others.
        tabs: Array.from(main.subs.entries())
          .sort(
            (first, second) =>
              second[1].coupons.length - first[1].coupons.length || first[1].order - second[1].order,
          )
          .map(([subId, sub]) => ({ id: subId, label: sub.label, coupons: sub.coupons })),
      }));
  }

  /**
   * Turn a list of the event's coupons (or products) into category tiles.
   *
   * One tile per distinct category, in the order the categories first appear, so the section only
   * ever shows categories this event actually carries. A child category is folded into its parent,
   * matching how the coupon sections below group themselves.
   *
   * `routeBase` is the page the tile links to: `categories` for coupon categories, `collection` for
   * product ones.
   */
  private toEventCategoryTiles(
    items: unknown[],
    routeBase: 'categories' | 'collection',
  ): PopularCategoryItem[] {
    const tiles = new Map<string, PopularCategoryItem>();

    for (const raw of items) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;

      const category = this.toRecord(item['category']);
      if (!category) continue;

      // Group under the parent when this is a child category, the same rule the coupon sections use.
      const parent =
        category['isChild'] === true ? this.toRecord(category['parentCategory']) : null;
      const target = parent ?? category;

      const label = String(target['name'] ?? '').trim();
      const slug = String(target['slug'] ?? '').trim();
      if (!label || !slug || tiles.has(slug)) continue;

      const iconType = String(target['iconType'] ?? '').trim();
      const iconImagePath = String(target['iconImagePath'] ?? '').trim();
      const iconCode = String(target['iconCode'] ?? '').trim();

      // No admin icon on the category? Borrow the artwork from the very item that put this category
      // on the page — a product's photo, or the store logo behind a coupon — so a tile is never an
      // empty circle.
      const fallbackImage =
        String(item['thumbnailImagePath'] ?? '').trim() ||
        String(this.toRecord(item['store'])?.['logoImagePath'] ?? '').trim();

      const imagePath = iconType === 'image' && iconImagePath ? iconImagePath : fallbackImage;

      tiles.set(slug, {
        label,
        slug,
        path: `/${routeBase}/${slug}`,
        imageUrl: imagePath ? this.assetUrl(imagePath) : '',
        iconHtml:
          iconType === 'code' && iconCode ? this.sanitizer.bypassSecurityTrustHtml(iconCode) : null,
      });
    }

    return Array.from(tiles.values());
  }

  private toRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  }

  private toEventCouponCard(coupon: FrontendCoupon): EventCouponCard {
    const store = coupon.store;
    const slug = store?.slug?.trim() ?? '';
    const logoPath = store?.logoImagePath?.trim() ?? '';
    const isDeal = coupon.isDeal === true;

    return {
      id: coupon.id ?? '',
      storeSlug: slug,
      store: store?.name ?? '',
      logoUrl: logoPath ? this.assetUrl(logoPath) : '',
      storePath: slug ? `/${slug}` : '#',
      title: coupon.name ?? '',
      expires: coupon.endDateText?.trim() || 'On Going',
      isDeal,
      actionLabel: isDeal ? 'Get Deal' : 'Reveal Code',
    };
  }

  /**
   * Show Code / Get Deal on an event coupon — the same two destinations the store and home pages
   * use, so a coupon behaves identically wherever it is clicked:
   *  - a NEW tab gets `/<store>/coupon/<id>`, which that store page turns into the offer popup, so
   *    the code stays on screen and the page can be shared;
   *  - THIS tab follows the `/go/:store/:id` redirect out to the merchant.
   *
   * Order matters: the new tab is opened first, while the browser still counts this as a user
   * gesture. Set `location` first and the pending `window.open` is discarded as the page unloads.
   *
   * Built from the coupon's own store, never the event — every coupon here belongs to a different
   * merchant. Previously these buttons were plain links to the store page, so a click never reached
   * the merchant at all and no affiliate hit was recorded.
   */
  protected openEventOffer(item: EventCouponCard): void {
    if (!item.id || !item.storeSlug) return;

    // No browser (SSR/prerender): nothing to navigate.
    if (typeof window === 'undefined') return;

    this.affiliate.openCoupon({
      storeSlug: item.storeSlug,
      couponId: item.id,
      store: item.store || item.storeSlug,
      kind: 'event-coupon',
    });
  }

  private mapEventCoupons(value: unknown): EventCouponCard[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return (
      value
        .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
        // The same card the category sections below render, so "Member Perks" is not a second coupon
        // design on one page. The admin's top picks arrive as loose records rather than typed
        // coupons, but the shape is the API's coupon payload, which is what the mapper reads.
        .map((coupon) => this.toEventCouponCard(coupon as unknown as FrontendCoupon))
    );
  }

  /** "Sep 30, 2026" / the admin's own text / "On Going" — the offer card adds the "Expires :" label. */
  private getCouponExpiry(coupon: Record<string, unknown>): string {
    const text = typeof coupon['endDateText'] === 'string' ? coupon['endDateText'].trim() : '';
    if (text) return text;

    const raw = typeof coupon['endDate'] === 'string' ? coupon['endDate'] : '';
    if (raw) {
      const date = new Date(raw);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        });
      }
    }

    return 'On Going';
  }

  private assetUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const base = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
  }

  private getContentString(content: Record<string, unknown> | undefined, key: string): string {
    const value = content?.[key];

    return typeof value === 'string' ? this.cleanText(value) : '';
  }

  private getContentHtml(content: Record<string, unknown> | undefined, key: string): string {
    const value = content?.[key];

    if (typeof value !== 'string' || !this.cleanText(value)) {
      return '';
    }

    return value.trim();
  }

  private cleanText(value: string): string {
    return value
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }
}
